import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { fractureAPI } from '../../services/api';

// /fracsim/sensitivity — PKN (前端实时) + P3D (后端批量)
const TH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.08)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  L: '#C2533A', w: '#2874A6', eff: '#1E8449', pn: '#6A4C93', h: '#D4770C',
  fontUI: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

// PKN 解析解 (Nordgren + Carter), 与后端 _simulate_pkn 同式
function pkn(p: { n: number; K: number; CL: number; E: number; nu: number; h: number; qMin: number; tMin: number }) {
  const gamma = 40;
  const mu = Math.max(1e-3, p.K * Math.pow(gamma, p.n - 1));
  const Ep = (p.E * 1e9) / (1 - p.nu * p.nu);
  const q = p.qMin / 60 / 2;            // 单翼 m³/s
  const t = p.tMin * 60;
  const Lnl = 0.68 * Math.pow((Ep * q ** 3) / (mu * p.h ** 4), 0.2) * Math.pow(t, 0.8);
  const w0 = 2.5 * Math.pow((mu * q * q) / (Ep * p.h), 0.2) * Math.pow(t, 0.2);
  const kappa = (p.CL * Math.sqrt(Math.PI * t)) / Math.max(w0, 1e-4);
  const eff = Math.min(0.98, Math.max(0.05, 1 / (1 + 2.4 * kappa)));
  const Lv = (eff * q * t) / ((Math.PI ** 2 / 16) * w0 * p.h);
  return { L: Math.min(Lv, Lnl * 1.2), w0_mm: w0 * 1000, eff: eff * 100 };
}

type Base = { n: number; K: number; CL: number; E: number; nu: number; h: number; qMin: number; tMin: number };
type P3dRow = { x: number; L: number; w0_mm: number; eff: number; pn: number; h: number };
const VARS: { key: keyof Base; label: string }[] = [
  { key: 'K', label: '稠度 K' }, { key: 'n', label: '流变 n' },
  { key: 'CL', label: '滤失 C_L' }, { key: 'E', label: '模量 E' },
  { key: 'qMin', label: '排量 q' }, { key: 'h', label: '缝高 h' },
];

export default function FracSimSensitivityPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const [modelType, setModelType] = useState<'PKN' | 'P3D'>('PKN');
  const [base, setBase] = useState<Base>({ n: 0.5, K: 0.5, CL: 0.00015, E: 35, nu: 0.22, h: 35, qMin: 14, tMin: 90 });
  const [sweepVar, setSweepVar] = useState<keyof Base>('K');
  const [p3dResults, setP3dResults] = useState<P3dRow[] | null>(null);
  const [p3dLoading, setP3dLoading] = useState(false);
  const set = (k: keyof Base, v: number) => setBase((b) => ({ ...b, [k]: v }));

  // PKN 前端实时计算
  const pkRows = useMemo(() => {
    const b0 = base[sweepVar];
    const out: { x: number; L: number; w0_mm: number; eff: number }[] = [];
    for (let i = 0; i <= 20; i++) {
      const x = b0 * (0.5 + i * 0.05);
      const r = pkn({ ...base, [sweepVar]: x });
      out.push({ x, ...r });
    }
    return out;
  }, [base, sweepVar]);

  // P3D 后端批量计算
  const runP3DSweep = useCallback(async () => {
    if (!projectId) { message.warning('请先选择项目'); return; }
    setP3dLoading(true);
    try {
      const b0 = base[sweepVar];
      const values: number[] = [];
      for (let i = 0; i <= 20; i++) values.push(b0 * (0.5 + i * 0.05));
      const { data } = await fractureAPI.sensitivity({
        project_id: projectId,
        sweep_param: sweepVar === 'qMin' ? 'qMin' : sweepVar === 'tMin' ? 'tMin' : sweepVar,
        sweep_values: values,
        base_options: {
          model_type: 'P3D', fluid_n: base.n, fluid_k: base.K, leakoff_c: base.CL,
          youngs_modulus: base.E, poissons_ratio: base.nu, frac_height: base.h, n_steps: 8,
        },
      } as any);
      setP3dResults((data as any).results?.map((r: any) => ({
        x: r.sweep_value,
        L: r.half_length, w0_mm: r.w0_mm, eff: r.efficiency_pct,
        pn: r.net_pressure_mpa, h: r.height,
      })) || null);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'P3D 敏感性计算失败');
    } finally { setP3dLoading(false); }
  }, [projectId, base, sweepVar]);

  const rows = modelType === 'PKN' ? pkRows : (p3dResults || []);
  const showTable = modelType === 'PKN' || p3dResults;

  if (!currentProject) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>参数敏感性</h2>
        <select value={modelType} onChange={(e) => setModelType(e.target.value as 'PKN' | 'P3D')}
          style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: `1px solid ${TH.headerBorder}` }}>
          <option value="PKN">PKN · 前端实时</option>
          <option value="P3D">P3D · 后端批量</option>
        </select>
        {modelType === 'P3D' && (
          <button onClick={runP3DSweep} disabled={p3dLoading}
            style={{ padding: '4px 12px', borderRadius: 4, fontSize: 11, cursor: p3dLoading ? 'wait' : 'pointer',
              border: `1px solid ${TH.accent}`, background: TH.accent, color: '#fff' }}>
            {p3dLoading ? '计算中…' : '▶ 运行 P3D 扫描'}
          </button>
        )}
        <span style={{ fontSize: 12, color: TH.textMuted }}>
          {modelType === 'PKN' ? '前端实时, 固定其余参数扫描 0.5×~1.5×' : '后端批量 P3D (Simonson 缝高), 需先运行扫描'}
        </span>
      </div>

      {/* 基准参数 + 扫描变量 */}
      <div style={{ padding: '10px 20px', display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: `1px solid ${TH.headerBorder}`, background: TH.panelHeaderBg }}>
        {([['n', '流变 n', 0.05], ['K', '稠度 K', 0.05], ['CL', '滤失 C_L', 0.00005], ['E', '模量 E', 1], ['h', '缝高 h', 1], ['qMin', '排量 q m³/min', 1], ['tMin', '时间 t min', 5]] as const).map(([k, l, st]) => (
          <label key={k} style={{ fontSize: 11, color: TH.textSec, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {l}
            <input type="number" step={st} value={base[k as keyof Base]} onChange={(e) => set(k as keyof Base, +e.target.value)}
              style={{ width: 72, fontSize: 12, padding: '3px 6px', borderRadius: 3, border: `1px solid ${TH.headerBorder}` }} />
          </label>
        ))}
        <label style={{ fontSize: 11, color: TH.textSec, display: 'flex', flexDirection: 'column', gap: 2 }}>
          扫描变量
          <select value={sweepVar} onChange={(e) => setSweepVar(e.target.value as keyof Base)}
            style={{ fontSize: 12, padding: '3px 6px', borderRadius: 3, border: `1px solid ${TH.headerBorder}` }}>
            {VARS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select>
        </label>
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 300px', gap: 1, minHeight: 0, background: TH.headerBorder }}>
        <div style={{ background: TH.canvasBg }}><RespChart rows={rows} varLabel={VARS.find((v) => v.key === sweepVar)?.label || ''} /></div>
        <div style={{ background: TH.canvasBg, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: TH.panelHeaderBg, position: 'sticky', top: 0 }}>
              {['变量', '半缝长', modelType === 'P3D' ? '缝高' : null, '缝宽mm', '液效%'].filter(Boolean).map((h) => <th key={h!} style={{ padding: '5px 8px', textAlign: 'right', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.filter((_, i) => i % 2 === 0).map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #F0EDE4' }}>
                  <td style={td}>{r.x < 0.01 ? r.x.toExponential(1) : r.x.toFixed(2)}</td>
                  <td style={td}>{r.L.toFixed(0)}</td>
                  {modelType === 'P3D' && <td style={td}>{(r as any).h?.toFixed(0) ?? '—'}</td>}
                  <td style={td}>{r.w0_mm.toFixed(2)}</td>
                  <td style={td}>{r.eff.toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RespChart({ rows, varLabel }: { rows: { x: number; L: number; w0_mm: number; eff: number }[]; varLabel: string }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr; ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);
    if (!rows.length) return;
    const padL = 44, padR = 16, padT = 24, padB = 32;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    const xMin = rows[0].x, xMax = rows[rows.length - 1].x;
    const X = (x: number) => padL + ((x - xMin) / (xMax - xMin || 1)) * plotW;
    // 三条线各自归一化 [0,1] 看相对趋势
    const series: [keyof typeof rows[0], string, string][] = [['L', '半缝长', TH.L], ['w0_mm', '缝宽', TH.w], ['eff', '液效', TH.eff]];
    ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5;
    for (let k = 0; k <= 4; k++) { const y = padT + (k / 4) * plotH; ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke(); }
    series.forEach(([key, , color]) => {
      const vals = rows.map((r) => r[key] as number);
      const vMin = Math.min(...vals), vMax = Math.max(...vals);
      ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.beginPath();
      rows.forEach((r, i) => {
        const y = padT + plotH - (((r[key] as number) - vMin) / (vMax - vMin || 1)) * plotH;
        i === 0 ? ctx.moveTo(X(r.x), y) : ctx.lineTo(X(r.x), y);
      });
      ctx.stroke();
    });
    // 图例
    ctx.font = `10px ${TH.fontUI}`; let lx = padL + 4;
    series.forEach(([, label, color]) => {
      ctx.fillStyle = color; ctx.fillRect(lx, padT - 14, 12, 3);
      ctx.fillStyle = TH.textSec; ctx.textAlign = 'left'; ctx.fillText(label, lx + 15, padT - 10);
      lx += 70;
    });
    // x 轴
    ctx.fillStyle = TH.textMuted; ctx.font = `9px ${TH.fontMono}`; ctx.textAlign = 'center';
    [0, 0.5, 1].forEach((f) => { const x = xMin + (xMax - xMin) * f; ctx.fillText(x < 0.01 ? x.toExponential(1) : x.toFixed(2), X(x), H - padB + 14); });
    ctx.fillStyle = TH.textSec; ctx.font = `10px ${TH.fontUI}`;
    ctx.fillText(`${varLabel} (扫描)`, padL + plotW / 2, H - 6);
  }, [rows, varLabel]);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

const td: React.CSSProperties = { padding: '4px 8px', textAlign: 'right', fontFamily: TH.fontMono, color: TH.text };
